import { Box, IconButton, TextField, Typography } from '@mui/material';
import React, { useState, useEffect, useContext } from 'react';
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { ThemeContext } from '../../../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

const BuyStop = ({ price, setPrice, symbolId, stopLoss, setStopLoss, takeProfit, setTakeProfit, setDisablePlaceorder }) => {
  const urlParams = new URLSearchParams(window.location.search);
  const querySymbolId = urlParams.get("symbolId");
  const symbolIDToPass = symbolId || querySymbolId
  const { symbols } = useSelector(state => state.watchlist)
  const symbolData = symbols[symbolIDToPass] || {};
  const allSymbolParams = useSelector((state) => state.symbol.allSymbolParams);
  const symbolParameterData = allSymbolParams?.[symbolIDToPass] || null;

  const stopOffset = (symbolParameterData.stopOffset ?? 10) * (1 / Math.pow(10, symbolData.digits));
  const limitOffset = (symbolParameterData.limitOffset ?? 10) * (1 / Math.pow(10, symbolData.digits));
  const symbol = useSelector(state => state.updateSymbol.symbols?.[symbolData?.id])
  const { darkMode } = useContext(ThemeContext)
  const { t } = useTranslation()
  const [tpValidation, setTpValidation] = useState(0)
  const [slValidation, setSlValidation] = useState(0)
  const [atPriceValidation, setAtPriceValidation] = useState(0)

  const handlePriceChange = (isIncrease) => {
    setPrice((prev) => {
      let currentPrice = parseFloat(prev);
      if (isNaN(currentPrice)) {
        currentPrice = atPriceValidation ?? 0;
      }

      const step = 1 / Math.pow(10, symbolData?.digits || 2);
      const priceChange = isIncrease ? step : -step;
      const newPrice = Math.max(0, currentPrice + priceChange);
      //   return newPrice;
      return newPrice.toFixed(symbolData?.digits)
    });
  };

  const calculateStopLoss = () => {
    if (price === null) return null;
    return (Number(price) - stopOffset).toFixed(symbolData.digits);
  };

  const calculateTakeProfit = () => {
    if (price === null) return null;
    console.log(limitOffset)
    return (Number(price) + limitOffset).toFixed(symbolData.digits);
  };

  const calculateAtPrice = () => {
    const maxPrice = parseFloat((symbol.ask + stopOffset).toFixed(symbolData.digits))
    setAtPriceValidation(maxPrice);
    return maxPrice;
  }

  useEffect(() => {
    if (price) {
      setSlValidation(calculateStopLoss())
      setTpValidation(calculateTakeProfit())
    }

  }, [price])

  useEffect(() => {
    const isPriceValid = price > atPriceValidation;
    const isTPValid = !takeProfit || parseFloat(takeProfit) > tpValidation;
    const isSLValid = !stopLoss || parseFloat(stopLoss) < slValidation;

    if (isPriceValid && isTPValid && isSLValid) {
      setDisablePlaceorder(false);
    } else {
      setDisablePlaceorder(true);
    }
  }, [stopLoss, takeProfit, price, tpValidation, slValidation, atPriceValidation]);

  useEffect(() => {
    calculateAtPrice()
  }, [symbol])

  useEffect(() => {
    setPrice(calculateAtPrice())
  }, [symbolData, symbolIDToPass])

  const handleChangePrice = (value) => {
    const desimals = value.split('.')?.[1]
    if (!desimals) {
      setPrice(value);
      return;
    }
    if (desimals.length <= symbolData.digits) {
      setPrice(value)
    }
  }
  const handleChangeSL = (value) => {
    const desimals = value.split('.')?.[1]
    if (!desimals) {
      setStopLoss(value);
      return;
    }
    if (desimals.length <= symbolData.digits) {
      setStopLoss(value)
    }
  }

  const handleChangeTP = (value) => {
    const desimals = value.split('.')?.[1]
    if (!desimals) {
      setTakeProfit(value);
      return;
    }
    if (desimals.length <= symbolData.digits) {
      setTakeProfit(value)
    }
  }
  const handleSLChange = (isIncrease) => {
    setStopLoss((prev) => {
      let currentValue = parseFloat(prev);
      if (isNaN(currentValue) || currentValue === 0) {
        return slValidation;
      }
      const step = 1 / Math.pow(10, symbolData?.digits || 2);
      const newValue = isIncrease ? currentValue + step : currentValue - step;
      return Math.max(0, newValue).toFixed(symbolData?.digits);
    });
  };

  const handleTPChange = (isIncrease) => {
    setTakeProfit((prev) => {
      let currentValue = parseFloat(prev);
      if (isNaN(currentValue) || currentValue === 0) {
        return tpValidation;
      }
      const step = 1 / Math.pow(10, symbolData?.digits || 2);
      const newValue = isIncrease ? currentValue + step : currentValue - step;
      return Math.max(0, newValue).toFixed(symbolData?.digits);
    });
  };

  const handleSLBlur = (e) => {
    const value = parseFloat(e.target.value);
    if (!value || isNaN(value)) return;

    const minSL = parseFloat(slValidation);
    if (value >= minSL) {
      setStopLoss(minSL.toFixed(symbolData.digits));
    } else {
      setStopLoss(value.toFixed(symbolData.digits));
    }
  };

  const handleTPBlur = (e) => {
    const value = parseFloat(e.target.value);
    if (!value || isNaN(value)) return;

    const minTP = parseFloat(tpValidation);
    if (value <= minTP) {
      setTakeProfit(minTP.toFixed(symbolData.digits));
    } else {
      setTakeProfit(value.toFixed(symbolData.digits));
    }
  };

  const handlePriceBlur = (e) => {
    const value = parseFloat(e.target.value);
    if (isNaN(value)) return;

    const minPrice = parseFloat(atPriceValidation);

    if (value > minPrice) {
      setPrice(minPrice.toFixed(symbolData.digits));
    } else {
      setPrice(value.toFixed(symbolData.digits));
    }
  };

  return (
    <>
      <Box>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography sx={{ fontSize: '0.75rem', color: darkMode ? 'var(--lightText)' : "var(--black)", }}>{t('price')}</Typography>
            <Typography variant="caption" align="right" sx={{ fontSize: '0.7rem', color: darkMode ? 'var(--lightText)' : "var(--black)", }}>
              {`${t('greaterThan')} ${atPriceValidation.toFixed(symbolData.digits) || 0}`}
            </Typography>
          </Box>

          {/* Price Input Field */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", border: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, overflow: "hidden" }}>
            <IconButton onClick={() => handlePriceChange(false)} sx={{ borderRight: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, borderRadius: 0, border: "none" }}>
              <RemoveIcon fontSize="small" sx={{ fontSize: '1rem', color: darkMode ? 'var(--lightText)' : "var(--black)" }} />
            </IconButton>

            <TextField value={price} type='number' inputProps={{ step: (1 / Math.pow(10, symbolData.digits)) }} sx={{ width: 120,"& .MuiInputBase-input": { textAlign: "center", }, "& .MuiOutlinedInput-notchedOutline": { border: "none" }, color: darkMode ? 'var(--lightText)' : "var(--black)" }} size="small" onChange={(e) => handleChangePrice(e.target.value)} />

            <IconButton onClick={() => handlePriceChange(true)} sx={{ borderLeft: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, borderRadius: 0, border: "none" }}>
              <AddIcon fontSize="small" sx={{ fontSize: '1rem', color: darkMode ? 'var(--lightText)' : "var(--black)" }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, gap: 2 }}>
        <Box>

          <Typography sx={{ fontSize: '0.75rem', color: darkMode ? 'var(--lightText)' : "var(--black)", }}>{t('stopLoss')} : <b style={{ fontSize: '0.7rem', }}>&lt;{slValidation}</b></Typography>
          <Box sx={{ display: "flex", alignItems: "center", border: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, overflow: "hidden", }} >
            <IconButton onClick={() => handleSLChange(false)} sx={{ borderRight: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, borderRadius: 0, border: "none", color: darkMode ? 'var(--lightText)' : "var(--black)", }} >
              <RemoveIcon fontSize="small" sx={{ fontSize: '1rem', color: darkMode ? 'var(--lightText)' : "var(--black)", }} />
            </IconButton>
            <TextField type='number' inputProps={{ step: (1 / Math.pow(10, symbolData.digits)), style: { textAlign: "center", fontSize: '0.9rem', padding: 0 } }} value={stopLoss} onChange={(e) => handleChangeSL(e.target.value)} size="small"
              sx={{ color: darkMode ? 'var(--lightText)' : "var(--black)","& .MuiInputBase-input": { textAlign: "center", }, "& .MuiOutlinedInput-notchedOutline": { border: "none" }, "& .MuiInputBase-root": { padding: 0 } }} />
            <IconButton onClick={() => handleSLChange(true)} sx={{ borderRight: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, borderRadius: 0, border: "none", color: darkMode ? 'var(--lightText)' : "var(--black)", }} >
              <AddIcon fontSize="small" sx={{ fontSize: '1rem', }} />
            </IconButton>

          </Box>
          {/* {stopLoss !== null && stopLoss !== undefined && stopLoss >= slValidation && (
                        <Typography sx={{ color: 'red', fontSize: '0.65rem', marginTop: 1 }}>{t('stopLossshouldbelessthan')} {slValidation}</Typography>
                    )} */}
        </Box>

        <Box>
          <Typography sx={{ fontSize: '0.75rem', color: darkMode ? 'var(--lightText)' : "var(--black)", }}>{t('takeProfit')} : <b style={{ fontSize: '0.7rem', }}> &gt; {tpValidation}</b></Typography>
          <Box sx={{ display: "flex", alignItems: "center", border: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, overflow: "hidden", }}>
            <IconButton onClick={() => handleTPChange(false)} sx={{ borderRight: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, borderRadius: 0, border: "none", color: darkMode ? 'var(--lightText)' : "var(--black)", }} >
              <RemoveIcon fontSize="small" sx={{ fontSize: '1rem', }} />
            </IconButton>
            <TextField type='number' value={takeProfit} onChange={(e) => handleChangeTP(e.target.value)} size="small" inputProps={{ min: 0, step: (1 / Math.pow(10, symbolData.digits)), style: { textAlign: "center", fontSize: '0.9rem', padding: 0 } }}
              sx={{"& .MuiInputBase-input": { textAlign: "center", }, "& .MuiOutlinedInput-notchedOutline": { border: "none" }, "& .MuiInputBase-root": { padding: 0 }, color: darkMode ? 'var(--lightText)' : "var(--black)", }} />
            <IconButton onClick={() => handleTPChange(true)} sx={{ borderRight: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'}`, borderRadius: 0, border: "none", color: darkMode ? 'var(--lightText)' : "var(--black)", }}>
              <AddIcon fontSize="small" sx={{ fontSize: '1rem', }} />
            </IconButton>
          </Box>

          {/* {takeProfit !== null && takeProfit !== undefined && takeProfit >= tpValidation && (
                        <Typography sx={{ color: 'red', fontSize: '0.65rem', marginTop: 1 }}>{t('takeProfitshouldbegreaterthan')} {tpValidation}</Typography>
                    )} */}
        </Box>
      </Box>
    </>
  );
};

export default BuyStop;
